import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CopyIcon,
  EditIcon,
  EyeIcon,
  EyeOffIcon,
  ListOrderedIcon,
  PlusIcon,
  SettingsIcon,
  ToggleLeftIcon,
  ToggleRightIcon,
} from "lucide-react";
import { useState } from "react";

import { useViewerHasPermission } from "@/auth/shared";
import {
  DropdownMenu,
  DropdownMenuItem,
} from "@/components/base/dropdown-menu";
import { addToast } from "@/components/base/toast";
import { ToggleRosterPublishedForm } from "@/components/tournaments/controls/toggle-roster-published";
import { useCopyPlayerEmails } from "@/hooks/tournament-mutations";
import { useTRPC } from "@/integrations/trpc/react";

import { AddTeamForm } from "./add-team";
import { SetCapacityForm } from "./set-capacity";

export function DivisionControlsDropdown({
  tournamentId,
  tournamentDivisionId,
  hasSeed,
  onSeedPress,
}: {
  tournamentId: number;
  tournamentDivisionId: number;
  hasSeed: boolean;
  onSeedPress: () => void;
}) {
  const canUpdate = useViewerHasPermission({ tournaments: ["update"] });
  const copyPlayerEmails = useCopyPlayerEmails();

  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate: setAutopromote } = useMutation({
    ...trpc.tournaments.setAutopromote.mutationOptions(),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(
        trpc.tournaments.get.queryOptions({ id: tournamentId }),
      );
      addToast({
        variant: "success",
        title: `Waitlist auto-promote ${variables.autopromoteWaitlist ? "on" : "off"}`,
      });
    },
  });

  const { data: division } = useQuery({
    ...trpc.tournaments.get.queryOptions({ id: tournamentId }),
    select: (data) =>
      data?.tournamentDivisions.find(({ id }) => id === tournamentDivisionId),
  });

  const [isCapacityOpen, setIsCapacityOpen] = useState(false);
  const [isAddTeamOpen, setIsAddTeamOpen] = useState(false);
  const [isPublishOpen, setIsPublishOpen] = useState(false);

  if (!canUpdate || !division) {
    return null;
  }

  return (
    <>
      <DropdownMenu label="Division controls" buttonIcon={<SettingsIcon />}>
        <DropdownMenuItem onAction={() => setIsCapacityOpen(true)}>
          <EditIcon className="size-4" />
          Set capacity ({division.capacity} / {division.waitlistCapacity} WL)
        </DropdownMenuItem>
        <DropdownMenuItem onAction={() => setIsAddTeamOpen(true)}>
          <PlusIcon className="size-4" />
          Add team
        </DropdownMenuItem>
        <DropdownMenuItem
          onAction={() =>
            copyPlayerEmails(tournamentId, [tournamentDivisionId])
          }
        >
          <CopyIcon className="size-4" />
          Copy emails
        </DropdownMenuItem>
        <DropdownMenuItem
          onAction={() =>
            setAutopromote({
              id: tournamentDivisionId,
              autopromoteWaitlist: !division.autopromoteWaitlist,
            })
          }
        >
          {division.autopromoteWaitlist ? (
            <ToggleRightIcon className="size-4" />
          ) : (
            <ToggleLeftIcon className="size-4" />
          )}
          Turn {division.autopromoteWaitlist ? "off" : "on"} WL auto-promote
        </DropdownMenuItem>
        <DropdownMenuItem onAction={() => setIsPublishOpen(true)}>
          {division.rosterPublished ? (
            <EyeOffIcon className="size-4" />
          ) : (
            <EyeIcon className="size-4" />
          )}
          {division.rosterPublished ? "Unpublish roster" : "Publish roster"}
        </DropdownMenuItem>
        <DropdownMenuItem onAction={onSeedPress}>
          <ListOrderedIcon className="size-4" />
          {hasSeed ? "Reseed division" : "Seed division"}
        </DropdownMenuItem>
      </DropdownMenu>

      <SetCapacityForm
        division={division}
        isOpen={isCapacityOpen}
        onOpenChange={setIsCapacityOpen}
        hideTrigger
      />
      <AddTeamForm
        tournamentId={tournamentId}
        division={division}
        isOpen={isAddTeamOpen}
        onOpenChange={setIsAddTeamOpen}
        hideTrigger
      />
      <ToggleRosterPublishedForm
        tournamentId={tournamentId}
        division={division}
        isOpen={isPublishOpen}
        onOpenChange={setIsPublishOpen}
      />
    </>
  );
}
